/**
 * Telegram WebApp bootstrap — the typed surface of the injected SDK.
 *
 * This module only knows about Telegram. Theme projection lives in
 * `src/theme/*` (spec §1) and native chrome — BackButton / MainButton /
 * haptics — in `src/lib/telegram-ui.ts`; both call in here for the handle.
 * Keeping the dependency one-way avoids an import cycle at boot.
 *
 * Outside Telegram every accessor returns null and callers fall back to
 * ordinary web behaviour (own buttons, OS colour scheme).
 */

export interface TelegramMainButton {
  text: string;
  isVisible: boolean;
  isActive: boolean;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  setText(text: string): void;
  setParams(params: {
    text?: string;
    color?: string;
    text_color?: string;
    is_active?: boolean;
    is_visible?: boolean;
  }): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

export interface TelegramBackButton {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

export interface TelegramHapticFeedback {
  impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; username?: string; first_name?: string; last_name?: string };
  };
  themeParams: Record<string, string>;
  colorScheme: "light" | "dark";
  version: string;
  viewportHeight: number;
  viewportStableHeight: number;
  MainButton: TelegramMainButton;
  BackButton: TelegramBackButton;
  HapticFeedback: TelegramHapticFeedback;
  expand(): void;
  ready(): void;
  close(): void;
  onEvent(eventType: string, cb: () => void): void;
  offEvent(eventType: string, cb: () => void): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getTelegram(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function isInTelegram(): boolean {
  const tg = getTelegram();
  return !!(tg && tg.initData);
}

/**
 * Tell Telegram we are ready and take the full viewport. Theme application is
 * a separate step (`initTheme`) that main.tsx runs right after this.
 */
export function initTelegram(): void {
  const tg = getTelegram();
  if (!tg) return;
  tg.ready();
  tg.expand();
}
